import type { FileError } from 'react-dropzone';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { Grid, Stack, Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import { isNotEmptyArray, isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import SampleTable from '../../../../../assets/images/data-metering-table-sample.svg';
import { DROP_ZONE_CONFIG } from '../../data-metering-consts/upload-dialog-consts';
import { DataMeteringFilePreview } from './DataMeteringFilePreview';
import type { HandleDialogSubmitParams } from './UploadMenuItem';

type UploadDialogContentProps = {
  setValid: (isValid: boolean) => void;
};

export const UploadDialogContent: React.FC<UploadDialogContentProps> = ({ setValid }) => {
  const [errorMessage, setErrorMessage] = useState<FileError | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const { setValue } = useFormContext<Partial<HandleDialogSubmitParams>, unknown, HandleDialogSubmitParams>();

  const handleDelete = () => {
    setFile(null);
    setValid(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...DROP_ZONE_CONFIG,
    onDrop: (acceptedFiles, fileRejections) => {
      if (isNotEmptyArray(fileRejections)) {
        const { errors } = fileRejections[0];
        setErrorMessage(errors[0]);

        return;
      }
      
      setErrorMessage(null);
      setFile(acceptedFiles[0]);
      setValue('file', acceptedFiles[0]);
    },
  });

  const getBackgroundColor = () => {
    if (isDragActive) {
      return '#3A78F814';
    }
    if (isNotNullOrUndefined(errorMessage)) {
      return '#D32F2F0A';
    }

    return 'transparent';
  };

  const getBorderColor = () => {
    if (isDragActive) {
      return 'primary.main';
    }
    if (isNotNullOrUndefined(errorMessage)) {
      return 'error.main';
    }

    return 'divider';
  };

  return ( isNotNullOrUndefined(file) ? 
    <DataMeteringFilePreview
      file={file}
      handleDelete={handleDelete}
      setValid={setValid}
    />
    : (
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            padding={5}
            border="1px dashed"
            borderColor={getBorderColor()}
            borderRadius={1}
            width="100%"
            {...getRootProps()}
            sx={{
              backgroundColor: getBackgroundColor(),
              cursor: 'pointer',
              maxWidth: 632,
            }}
          >
            <input {...getInputProps()} />

            <VyIcon
              icon="UploadFileFilled"
              color='#3A78F8'
            />
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Typography
                variant="subtitle1"
                color="primary.main"
                sx={{
                  margin: 0.5,
                  '&::after': {
                    content: '""',
                    display: 'block',
                    width: '100%',
                    height: '1px',
                    backgroundColor: 'primary.main',
                  },
                }}
              >
                Click to upload
              </Typography>
              <Typography
                variant="body1"
                color="text.primary"
              >
                or drag and drop
              </Typography>
            </Stack>
            <Typography
              variant="body1"
              color={isNullOrUndefined(errorMessage) ? 'text.secondary' : 'error.main'}
              marginTop={1}
              fontWeight={400}
              fontSize={14}
            >
              {isNullOrUndefined(errorMessage) ? 'CSV File (max. 3MB)' : getErrorMessage(errorMessage)}
            </Typography>
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
        >
        </Grid>
        <Grid
          item
          xs={12}
        >
          <Stack
            sx={{
              padding: '16px 24px',
              backgroundColor: 'rgba(13, 27, 39, 0.04)',
              borderRadius: 1,
              overflow: 'hidden',
              gap: 1,
              display: 'inline-flex',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <VyIcon icon="Warning" />
              <Typography
                variant="body1"
                color="text.secondary"
                fontWeight={350}
              >
                CSV File must be properly formatted — see the example below:
              </Typography>
            </Stack>

            <img
              src={SampleTable}
              alt="example"
              style={{ pointerEvents: 'none' }}
            />
          </Stack>
        </Grid>
      </Grid>
    ));
};

function getErrorMessage(error: FileError) {
  switch (error.code) {
    case ErrorCode.FileInvalidType:
      return 'Unsupported file type';
    case ErrorCode.FileTooLarge:
      return 'File too large';
    case ErrorCode.TooManyFiles:
      return 'Only one file is allowed';
    default:
      return 'An error occurred while uploading the file.';
  }
}